import { useEffect, useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import {
	getAllMenus,
	getTranslationStrings,
	getThemeGeneralSettings,
	getLocales,
} from '@/lib/api';
import { getTheme, getFontFaceCSS } from '@/lib/theme';

import DataProvider from '@/contexts/data-context';
import ThemeProvider from '@/contexts/theme-context';
import { useGlobalContext } from '@/contexts/global-context';
import { getDecodedTheme } from '@/customizer';
import { withLocale } from '@/utils/i18n/withLocale';

// import Header from '@/next-components/header';
import Layout from '@/next-components/layout';

import { GoogleSearch, MainNav } from '@/components/renders';

import { blocksThemeControls } from '@/theming-loader';
import { templatesThemeControls } from '@/theming-loader';

const LANG_CODE = 'EN';
const DATA = {
	uri: '/en/search',
	language: { code: 'EN', locale: 'en_US' },
	translations: [
		{
			language: { locale: 'fr_FR', code: 'FR' },
			uri: '/fr/recherche',
		},
		{
			language: { locale: 'en_US', code: 'EN' },
			uri: '/en/search',
		},
	],
};

// remember: node can be undefined! see https://nextjs.org/docs/advanced-features/automatic-static-optimization#how-it-works
const SearchPage = ({
	node,
	menus,
	generalSettings,
	cssFonts,
	initialTheme,
	decodedTheme,
}) => {
	const { navIsOpen, setNavIsOpen } = useGlobalContext();
	const router = useRouter();

	// close menu when a new page gets rendered
	useEffect(() => {
		const handleRouteChange = (/*url*/) => setNavIsOpen(() => false);
		router.events.on('routeChangeComplete', handleRouteChange);

		// cleanup
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, []);

	return (
		<ThemeProvider
			initialTheme={initialTheme}
			decodedTheme={decodedTheme}
			blocksThemeControls={blocksThemeControls}
			templatesThemeControls={templatesThemeControls}
		>
			<DataProvider data={generalSettings}>
				<Layout cssFonts={cssFonts}>
					<Head>
						<title>Search</title>
					</Head>
					<MainNav
						uri={node?.uri}
						menu={{
							primary: menus?.header,
							secondary: menus?.secondary,
							social: menus?.social_header,
						}}
						isNavOpen={navIsOpen}
						openNav={() => setNavIsOpen(() => true)}
						closeNav={() => setNavIsOpen(() => false)}
						translations={node?.translations}
					/>
					<GoogleSearch title="Search" />
				</Layout>
			</DataProvider>
		</ThemeProvider>
	);
};

export default withLocale(SearchPage);

export async function getStaticProps() {
	// Get locales (needs to be before uri because we need defaultLocale)
	const { locales, defaultLocale } = await getLocales();
	const defaultLocaleCode = defaultLocale?.toUpperCase();

	// Get data from current uri
	const baseUrl =
		process.env.NEXT_URL ?? process.env.VERCEL_URL ?? 'localhost:3000';

	// Get theme general settings
	const generalSettingsPromise = getThemeGeneralSettings(
		LANG_CODE || defaultLocaleCode
	);

	const [generalSettings] = await Promise.all([generalSettingsPromise]);

	// Get menus => WP menu locations as 2nd parameter
	const menusPromise = getAllMenus(LANG_CODE, [
		'HEADER',
		'SECONDARY',
		'SOCIAL_HEADER',
	]);

	// Get translation strings (if no locales, it will return and empty object)
	const translationStringsPromise = getTranslationStrings(locales);

	const themePromise = getTheme();

	const [menus, translationStrings, initialTheme] = await Promise.all([
		menusPromise,
		translationStringsPromise,
		themePromise,
	]);

	const decodedTheme = getDecodedTheme(
		initialTheme,
		blocksThemeControls,
		templatesThemeControls
	);

	return {
		// NOTE: never return `undefined` for a property, as it would fail to serialize to JSON - return `null` instead
		props: {
			node: { ...DATA, baseUrl },
			menus: menus ? menus : null,
			translationStrings: translationStrings ? translationStrings : null, // used in withLocale
			generalSettings: generalSettings ? generalSettings : null,
			cssFonts: initialTheme.tokens.fonts
				? getFontFaceCSS(initialTheme.tokens.fonts)
				: null,
			defaultLocale,
			locales,
			initialTheme,
			decodedTheme,
		},
		// TODO: maybe we can avoid all this regeneration?
		//       would be nice to only regenerate when there's recent changes in WP
		// Next.js will attempt to re-generate the page:
		// - When a request comes in
		// - At most once every x seconds
		revalidate: 15, // In seconds
	};
}
